import { useMemo } from 'react';
import { useFavoriteProfessionals } from '~/domain/FavoriteProfessionals';
import { usePaymentEligibility } from './usePaymentEligibility';

export interface IAutoConfirmDefaults {
  favorites: boolean;
  eligibleProfessionals: boolean;
}

export interface IUseAutoConfirmDefaultsResult {
  /**
   * Office cannot pay and eligibility has resolved — gates the invite / auto-confirm UI.
   * Passed straight through from `usePaymentEligibility` (defaults to `false` while the
   * payment query is in flight) so consumers share one payment-lock rule.
   */
  isPaymentLocked: boolean;
  /** Office has at least one favorite professional. */
  hasFavorites: boolean;
  /** Favorites are still loading; defer any favorites-dependent decision. */
  favoritesLoading: boolean;
  /** Favorites toggle should render inactive (resolved: no favorites). */
  favoritesDisabled: boolean;
  /** ON values for enforced auto-confirm — favorites only when the office has any. */
  defaultAutoConfirm: IAutoConfirmDefaults;
}

/** Payment lock and favorites state that drive the Auto-Confirm toggles on the shift request form. */
export const useAutoConfirmDefaults = (officeId?: string): IUseAutoConfirmDefaultsResult => {
  const { isPaymentLocked } = usePaymentEligibility(officeId);

  const { data: favorites, isLoading: favoritesLoading } = useFavoriteProfessionals({
    officeId: officeId ?? '',
    enabled: Boolean(officeId),
  });
  const hasFavorites = (favorites ?? []).length > 0;

  const favoritesDisabled = !favoritesLoading && !hasFavorites;

  // Stable identity so consumers can safely use it as an effect dependency.
  const defaultAutoConfirm = useMemo<IAutoConfirmDefaults>(
    () => ({ eligibleProfessionals: true, favorites: hasFavorites }),
    [hasFavorites],
  );

  return {
    isPaymentLocked,
    hasFavorites,
    favoritesLoading,
    favoritesDisabled,
    defaultAutoConfirm,
  };
};
